import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from "@angular/router";
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as FileSaver from 'file-saver';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ToastService } from '../../../../../main/services';
import { Tender } from '../../../../models';
import {
    TenderBidXlsImportService,
    TenderContractsService,
    TenderLaunchingXlsExportService,
    TenderSuppliersService
} from '../../../../services';


@Component({
    selector: 'jft-tender-bid-import-modal',
    templateUrl: './tender-bid-import-modal.component.html',
    styleUrls: ['./tender-bid-import-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderBidImportModalComponent implements OnInit, OnDestroy {
    public form: UntypedFormGroup;
    public loading: boolean = false;
    public loadingTemplate: boolean = false;
    public isDeleteOld: boolean = false;
    public isReplaceAll: boolean = false;
    public templateFileVersion: number = null;
    public currentFile: File = null;

    // Progress Tracking
    public isProcessing: boolean = false;
    public processingStep: string = '';
    public processingProgress: number = 0;

    public supplierSearchTerm = '';
    public supplierCompanyId: number | null = null;
    public supplierCompanyName: string | null = null;
    public supplierUserId: number | null = null;
    public supplierUserName: string | null = null;
    public supplierUserEmail: string | null = null;
    public suppliers: any[] = [];

    // ng-select state and streams
    public selectedSupplierUser: any = null;
    public supplierUserSearch$ = new BehaviorSubject<string>('');
    public supplierUsers$: Observable<any[]>;
    public loadingUsers = false;
    public hasPreloaded = false;

    public isBackgroundUploading: boolean = true;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private toast: ToastService,
        private tender: Tender,
        private service: TenderContractsService,
        private activeModal: NgbActiveModal,
        private supplierService: TenderSuppliersService,
        private tenderBidXlsImportService: TenderBidXlsImportService,
        private tenderExportXlsService: TenderLaunchingXlsExportService,
    ) {
    }

    public ngOnInit() {
        this.getSettings();
        // Set up the search stream for server-side search
        this.setupSearchStream();
    }

    private setupSearchStream() {
        // ng-select: wire search stream for server-side search
        this.supplierUsers$ = this.supplierUserSearch$.pipe(
            debounceTime(250),
            distinctUntilChanged(),
            switchMap((term: string) => {
                this.loadingUsers = true;
                // Set the search term in the service
                this.supplierService.setFilter(term || '');
                // Fetch users from server with search term
                return this.supplierService.fetchInvitedSupplierUsers(this.tender.id).pipe(
                    map((users: any[]) => {
                        // Sort alphabetically by company name
                        return users.sort((a, b) => {
                            const companyA = a.company?.name?.toLowerCase() || '';
                            const companyB = b.company?.name?.toLowerCase() || '';
                            return companyA.localeCompare(companyB);
                        });
                    }),
                    catchError(() => of([])),
                    finalize(() => this.loadingUsers = false)
                );
            })
        );
    }

    public getFormGroupErrorClasses(control: AbstractControl): {} {
        return {'is-invalid': control.invalid && control.touched};
    }

    public showErrorBlock(control: AbstractControl): boolean {
        return control.errors && (control.dirty || control.touched);
    }

    public close() {
        this.activeModal.dismiss('close');
    }

    public submitSupplier() {
    }

    public exportTenderInfo($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        if (this.loadingTemplate){
            return;
        }
        if (!this.supplierCompanyId || !this.supplierUserId) {
            this.toast.warning('Please select a supplier user first.');
            return;
        }
        this.loadingTemplate = true;
        this.tenderExportXlsService.fetchTemplate(this.tender, this.supplierCompanyId, this.supplierUserId)
            .pipe(
                tap(() => this.loadingTemplate = false),
                finalize(() => this.loadingTemplate = false)
            )
            .subscribe({
                next: (result) => {
                    if (result.type === 'task') {
                        this.close();
                        this.toast.success(
                            'Template will be ready for download in the notification section',
                            'Requested Template',
                        );
                        return;
                    }
                    const suffix = this.supplierCompanyName ? `-${this.supplierCompanyName}` : '';
                    const safeSuffix = suffix.replace(/[^a-zA-Z0-9 _\-\.]/g, ' ').replace(/\s+/g, ' ').trim();
                    FileSaver.saveAs(result.blob, `${this.tender.name}-tender-information-${safeSuffix ? ' ' + safeSuffix : ''}.xlsx`);
                },
                error: (e) => {
                    this.toast.error(e);
                }
            });
    }





    public trackUser(index: number, user: any): any {
        return user ? user.id : index;
    }

    public onDropdownOpen() {
        // No action needed since we use BehaviorSubject for initial load
    }

    public onSupplierSelected(user: any) {
        // Selected value is a User; capture user and company
        this.supplierUserId = user && (user.id || user['id']) ? (user.id || user['id']) : null;
        const comp = user && user.company;
        this.supplierCompanyId = comp && (comp.id || comp['id']) ? (comp.id || comp['id']) : null;
        this.supplierCompanyName = comp && (comp.name || comp['name']) ? (comp.name || comp['name']) : null;
        const first = user && (user.firstName || user['first_name']) || '';
        const last = user && (user.lastName || user['last_name']) || '';
        this.supplierUserName = `${first} ${last}`.trim();
        this.supplierUserEmail = user && (user.email || user['email']) || null;
    }

    public onSupplierInputBlur($event: any) {
        // Close any open popup and prevent reopening when a supplier is already selected
        if (this.supplierCompanyId) {
            const input = $event && $event.target as HTMLInputElement;
            if (input) {
                input.blur();
            }
        }
    }

    public ngOnDestroy() {
        // Clear any residual filter so other pages using the service are unaffected
        this.supplierService.setFilter('');
    }

    public reloadPage() {
        window.location.reload(); // todo refactor
    }

    public async filesSelected(files: File[]) {
        if (!files.length) {
            return;
        }

        this.currentFile = files[0];

        // Instead of processing here, we set the pending file and delete option in the service
        // and navigate to the overview page where the processing overlay will be shown.
        this.tenderBidXlsImportService.setPendingFile(this.currentFile, this.isDeleteOld);
        this.activeModal.close();
        this.router.navigate(['/tenders', this.tender.id, 'import', 'bid']);
    }



    public getSettings(){
        this.tenderExportXlsService.fetchSettings()
            .pipe()
            .subscribe(data => {
                this.templateFileVersion = data['file_version'];
            });
    }

}
